/**
 * The three writes on Payroll (WALLIYO-0131): opening a month, holding or
 * releasing a line, and closing.
 *
 * Each asks for a reason, because each is about money and the reason is what
 * the audit entry carries. Closing says plainly what it will do before it does
 * it: a held line stays held, and every ready one becomes paid.
 */

import type { PayrollLine } from "@wallyo/contracts/console/hr";
import { useState } from "react";
import { closePayroll, editPayrollLine, openPayroll } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t } from "../../i18n";
import { ReasonDialogView, useIntentWrite } from "../team/TeamDialogs";
import { amount } from "./PayrollView";

type Common = { onClose: () => void; onReload: () => void; onDone: (message: string) => void };

/** This month, as YYYY-MM, which is what a run is opened for by default. */
export function thisMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function OpenPayrollDialog(props: Common) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [month, setMonth] = useState(thisMonth());
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("payroll.openForm.title")}
      body={<p className="meta">{t("payroll.openForm.help")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("payroll.openForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          /^\d{4}-\d{2}$/.test(month) ? null : t("payroll.openForm.month"),
          (key) => openPayroll(client, { month, reason: reason.trim() }, { idempotencyKey: key }),
          ({ run, lines }) =>
            props.onDone(t("payroll.opened", { month: run.periodMonth.slice(0, 7), count: String(lines.length) })),
        )
      }
    >
      <div className="field">
        <label htmlFor="payroll-month">{t("payroll.openForm.month")}</label>
        <input
          id="payroll-month"
          type="month"
          value={month}
          onChange={(event) => {
            write.edited();
            setMonth(event.currentTarget.value);
          }}
        />
      </div>
    </ReasonDialogView>
  );
}

export function HoldLineDialog(props: Common & { line: PayrollLine; hold: boolean }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");
  const { line, hold } = props;

  return (
    <ReasonDialogView
      title={t("payroll.holdForm.title")}
      body={
        <p className="meta">
          {line.name} · {amount(line.net)}
          <br />
          {hold ? t("payroll.holdForm.hold") : t("payroll.holdForm.release")}
        </p>
      }
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={hold ? t("payroll.hold") : t("payroll.release")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reason.trim().length === 0 ? t("payroll.holdForm.title") : null,
          (key) =>
            editPayrollLine(
              client,
              line.payrollLineId,
              { hold, reason: reason.trim(), version: line.version },
              { idempotencyKey: key },
            ),
          ({ line: changed }) =>
            props.onDone(
              changed.status === "held"
                ? t("payroll.held", { name: changed.name })
                : t("payroll.released", { name: changed.name }),
            ),
        )
      }
    />
  );
}

export function ClosePayrollDialog(props: Common & { payrollRunId: string; version: number }) {
  const { client } = useApi();
  const write = useIntentWrite();
  const [reason, setReason] = useState("");

  return (
    <ReasonDialogView
      title={t("payroll.closeForm.title")}
      body={<p className="meta">{t("payroll.closeForm.help")}</p>}
      reason={reason}
      onReason={(next) => {
        write.edited();
        setReason(next);
      }}
      submitLabel={t("payroll.closeForm.submit")}
      pending={write.pending}
      problem={write.problem}
      error={write.error}
      onClose={props.onClose}
      onReload={props.onReload}
      onSubmit={() =>
        void write.run(
          reason.trim().length === 0 ? t("payroll.closeForm.title") : null,
          (key) =>
            closePayroll(
              client,
              props.payrollRunId,
              { reason: reason.trim(), version: props.version },
              { idempotencyKey: key },
            ),
          () => props.onDone(t("payroll.closed")),
        )
      }
    />
  );
}
